import {
  collection,
  deleteDoc,
  DocumentData,
  DocumentReference,
  onSnapshot,
  QueryDocumentSnapshot
} from '@firebase/firestore';
import {
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Box,
  TableContainer,
  Button,
  IconButton,
  CircularProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { format } from 'date-fns';
import { useState, useEffect, useCallback, memo } from 'react';
import { useNavigate } from 'react-router';
import { PaymentType } from 'src/api/purchases/data/enum/payment-type.enum';
import { IFailedOrder } from 'src/api/purchases/data/interfaces/failed-order.interface';
import { fixFailedOrderUsecase } from 'src/api/purchases/usecases/fix-failed-order.usecase';
import { firestoreDb } from 'src/constants';
import { SaveChangesDialog } from 'src/components/SaveChangesDialog';

const PaymentTypeText: Record<PaymentType, string> = {
  [PaymentType.Purchase]: 'Purchase',
  [PaymentType.Refill]: 'Top up',
  [PaymentType.Refund]: 'Refund'
};

type FailedOrderWithRef = IFailedOrder & {
  ref: DocumentReference<DocumentData>;
  id: string;
};

function AlertBanner() {
  const [orders, setOrders] = useState<FailedOrderWithRef[]>([]);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [recordToRemove, setRecordToRemove] = useState<FailedOrderWithRef | null>(null);
  const [fixingAll, setFixingAll] = useState(false);

  useEffect(() => {
    const collectionRef = collection(firestoreDb, 'failedOrders');
    const unsub = onSnapshot(collectionRef, (snap) => {
      const next = snap.docs.map((doc: QueryDocumentSnapshot<DocumentData>) => ({
        ...(doc.data() as IFailedOrder),
        ref: doc.ref,
        id: doc.id
      }));
      setOrders(next);
    });
    return () => unsub();
  }, []);

  const fixAll = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (!orders.length) return;
      setFixingAll(true);
      try {
        await fixFailedOrderUsecase(orders);
      } finally {
        setFixingAll(false);
      }
    },
    [orders]
  );

  const confirmRemove = useCallback(
    (e: React.MouseEvent, order: FailedOrderWithRef) => {
      e.stopPropagation();
      setRecordToRemove(order);
      setRemoveDialogOpen(true);
    },
    []
  );

  const deleteFailedOrder = useCallback(async () => {
    if (!recordToRemove) return;
    await deleteDoc(recordToRemove.ref);
    setRemoveDialogOpen(false);
    setRecordToRemove(null);
  }, [recordToRemove]);

  if (!orders.length) return null;

  return (
    <Box sx={sx.banner}>
      <Accordion elevation={1} sx={sx.accordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.expandIcon} />}>
          <Box sx={sx.header}>
            <Typography sx={sx.summaryTitle}>
              {`Failed orders found in the last 24h (${orders.length})`}
            </Typography>
            <Button
              sx={sx.smallText}
              onClick={fixAll}
              disabled={fixingAll}
              startIcon={
                fixingAll ? (
                  <CircularProgress size={16} thickness={5} color="inherit" />
                ) : undefined
              }
            >
              {fixingAll ? 'Fixing…' : 'Try fixing all'}
            </Button>
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={sx.details}>
          <TableContainer sx={sx.tableContainer}>
            <Table size="small">
              <TableBody>
                {orders.map((order) => (
                  <FailedOrderRow
                    key={order.id}
                    order={order}
                    onRemove={confirmRemove}
                    fixingAll={fixingAll}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      <SaveChangesDialog
        open={removeDialogOpen}
        isLoading={false}
        onClose={() => setRemoveDialogOpen(false)}
        onSubmit={deleteFailedOrder}
      />
    </Box>
  );
}

const FailedOrderRow = memo(function FailedOrderRow({
  order,
  onRemove,
  fixingAll
}: {
  order: FailedOrderWithRef;
  onRemove: (e: React.MouseEvent, order: FailedOrderWithRef) => void;
  fixingAll: boolean;
}) {
  const navigate = useNavigate();
  const [fixingOne, setFixingOne] = useState(false);

  const navigateToUser = useCallback(
    (userId: string) => {
      navigate(`/management/profile/details/${userId}`);
    },
    [navigate]
  );

  const fixOne = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setFixingOne(true);
      try {
        await fixFailedOrderUsecase([order]);
      } finally {
        setFixingOne(false);
      }
    },
    [order]
  );

  const rowDisabled = fixingAll;
  const buttonDisabled = !!order.processing || fixingAll || fixingOne;
  const showRowSpinner = fixingAll || fixingOne;

  return (
    <TableRow sx={{ ...sx.tableRow, ...(rowDisabled ? sx.rowDisabled : {}) }} onClick={() => navigateToUser(order.userId)}>
      <TableCell sx={sx.statusCell}>
        <Box sx={sx.statusBox}>
          {order.processing && <AutorenewIcon sx={sx.iconWhite} fontSize="small" />}
          {order.seen && <VisibilityIcon sx={sx.iconWhite} fontSize="small" />}
        </Box>
      </TableCell>

      <TableCell>
        <Typography sx={sx.textCell}>{order.deliveryEmail}</Typography>
      </TableCell>

      <TableCell>
        <Typography sx={sx.textCell}>{order.packageId}</Typography>
        <Typography align="center" sx={sx.smallText}>
          {order.transactionId}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography sx={sx.textCell}>
          {PaymentTypeText[order.paymentType]}
        </Typography>
      </TableCell>

      <TableCell sx={sx.paddedCell}>
        <Typography align="center" sx={sx.smallText}>
          {format(order.purchaseDateMillis, 'dd MMM h:mm:ss')}
        </Typography>
      </TableCell>

      <TableCell sx={{ ...sx.paddedCell, ...sx.fixed150 }}>
        <Button
          disabled={buttonDisabled}
          sx={sx.smallText}
          onClick={fixOne}
          startIcon={
            showRowSpinner ? (
              <CircularProgress size={16} thickness={5} color="inherit" />
            ) : undefined
          }
        >
          {showRowSpinner ? 'Fixing…' : 'Try fixing now'}
        </Button>
      </TableCell>

      <TableCell sx={{ ...sx.paddedCell, ...sx.fixed50 }}>
        <IconButton onClick={(e) => onRemove(e, order)} disabled={fixingAll}>
          <RemoveCircleOutlineIcon sx={sx.iconWhite} fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
});

export default AlertBanner;

const sx = {
  banner: {
    position: 'sticky',
    top: 100,
    zIndex: (theme: any) => theme.zIndex.appBar,
    mx: 2,
    my: 1.25
  },
  accordion: {
    bgcolor: (theme: any) => theme.palette.error.light,
    color: (theme: any) => theme.palette.common.white,
    borderRadius: 2,
    '&:before': { display: 'none' }
  },
  expandIcon: {
    color: (theme: any) => theme.palette.common.white
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
    pr: 5
  },
  summaryTitle: {
    fontWeight: 700,
    fontSize: 16
  },
  details: { p: 0 },
  tableContainer: { px: 1 },
  tableRow: {
    cursor: 'pointer',
    transition: 'opacity 150ms ease',
    '&:hover': {
      bgcolor: (theme: any) =>
        (theme.palette as any).error?.lighter ?? theme.palette.error.light
    }
  },
  rowDisabled: {
    opacity: 0.7,
    pointerEvents: 'none'
  },
  statusCell: { width: 72 },
  statusBox: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 1
  },
  textCell: {
    fontWeight: 500,
    textAlign: 'center',
    color: (theme: any) => theme.palette.common.white
  },
  smallText: {
    fontSize: 12,
    color: (theme: any) => theme.palette.common.white
  },
  paddedCell: { p: 1 },
  fixed150: { width: 150 },
  fixed50: { width: 50 },
  iconWhite: {
    color: (theme: any) => theme.palette.common.white
  }
};